import { Loader2 } from 'lucide-react';
import React from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { usePartnerProfile } from '../../../hooks/usePartnerSettings';
import { AutomationBuilderExamplePrompts } from './AutomationBuilderExamplePrompts/AutomationBuilderExamplePrompts';
import { useExamplePromptDrafts } from './AutomationBuilderExamplePrompts/useExamplePromptDrafts';
import {
  DEACTIVATE_EMAIL_SMS_TOGGLE,
  ENGAGE_SETTINGS_COPY,
  ONBOARDING_AUTOMATIONS_DEFAULT_TOGGLE,
} from './constants';
import styles from './EngageSettings.module.css';
import { OnboardingToggle } from './OnboardingToggle';
import { SaveBar } from './SaveBar';
import { SettingToggle } from './SettingToggle';

/** Only the toggles changed since the last save, so a refetch never reverts them. */
interface ToggleEdits {
  onboarding?: boolean;
  showDeactivateEmailSmsButton?: boolean;
  onboardingAutomationsEnabledByDefault?: boolean;
}

const editsChangedSince = (current: ToggleEdits, submitted: ToggleEdits): ToggleEdits =>
  Object.fromEntries(
    Object.entries(current).filter(([key, value]) => submitted[key as keyof ToggleEdits] !== value),
  );

/**
 * Engage Settings Tab Component
 * Settings and configuration for Engage features
 */
export const EngageSettingsTab: React.FC = () => {
  const { partner, isLoading, updateProfile, isUpdating } = usePartnerProfile();
  const [toggleEdits, setToggleEdits] = React.useState<ToggleEdits>({});
  const examplePrompts = useExamplePromptDrafts(
    partner?.featureCustomizations?.engage?.automationBuilderExamplePrompts ?? [],
  );

  const savedToggles: Required<ToggleEdits> = {
    onboarding: partner?.featureCustomizations?.enableFeature?.engage?.onboarding ?? true,
    showDeactivateEmailSmsButton:
      partner?.featureCustomizations?.engage?.showDeactivateEmailSmsButton === true,
    onboardingAutomationsEnabledByDefault:
      partner?.featureCustomizations?.engage?.onboardingAutomationsEnabledByDefault ?? true,
  };
  const toggles = { ...savedToggles, ...toggleEdits };
  const changedToggles = editsChangedSince(toggleEdits, savedToggles);
  const togglesAreDirty = Object.keys(changedToggles).length > 0;
  const settingsAreDirty = togglesAreDirty || examplePrompts.isDirty;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const promptValidation = examplePrompts.isDirty ? examplePrompts.validateAndShowErrors() : undefined;
    const promptsBlockSave = promptValidation?.valid === false;
    if (promptsBlockSave) return;

    const submittedPrompts = promptValidation?.valid ? promptValidation.prompts : undefined;
    const submittedEdits = changedToggles;
    const { onboarding, ...engageToggleEdits } = submittedEdits;
    const engageEdits = {
      ...engageToggleEdits,
      ...(submittedPrompts && { automationBuilderExamplePrompts: submittedPrompts }),
    };
    const engageSettingsChanged = Object.keys(engageEdits).length > 0;
    updateProfile(
      {
        featureCustomizations: {
          ...(onboarding !== undefined && { enableFeature: { engage: { onboarding } } }),
          ...(engageSettingsChanged && { engage: engageEdits }),
        },
      },
      {
        onSuccess: updatedPartner => {
          setToggleEdits(current => editsChangedSince(current, submittedEdits));
          if (submittedPrompts) {
            examplePrompts.markSaved(
              updatedPartner.featureCustomizations?.engage?.automationBuilderExamplePrompts ??
                submittedPrompts,
            );
          }
        },
      },
    );
  };

  const handleCancel = () => {
    setToggleEdits({});
    examplePrompts.reset();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={styles.engageSettings} noValidate>
      <SaveBar isDirty={settingsAreDirty} isSaving={isUpdating} onCancel={handleCancel} />
      <Card>
        <CardContent className={styles.toggleSettings}>
          <header>
            <h3>{ENGAGE_SETTINGS_COPY.title}</h3>
            <p>{ENGAGE_SETTINGS_COPY.description}</p>
          </header>

          <OnboardingToggle
            checked={toggles.onboarding}
            onChange={onboarding => setToggleEdits(current => ({ ...current, onboarding }))}
          />

          <SettingToggle
            {...DEACTIVATE_EMAIL_SMS_TOGGLE}
            checked={toggles.showDeactivateEmailSmsButton}
            onChange={showDeactivateEmailSmsButton =>
              setToggleEdits(current => ({ ...current, showDeactivateEmailSmsButton }))
            }
          />

          <SettingToggle
            {...ONBOARDING_AUTOMATIONS_DEFAULT_TOGGLE}
            checked={toggles.onboardingAutomationsEnabledByDefault}
            onChange={enabled =>
              setToggleEdits(current => ({
                ...current,
                onboardingAutomationsEnabledByDefault: enabled,
              }))
            }
          />
        </CardContent>
      </Card>
      <AutomationBuilderExamplePrompts
        drafts={examplePrompts.drafts}
        setDrafts={examplePrompts.setDrafts}
        showErrors={examplePrompts.showErrors}
        isSaving={isUpdating}
      />
    </form>
  );
};
